<template>
    <div class="wc-page">
        <BackendHeader />

        <div class="wc-container">
            <div class="page-header">
                <h2>Watched Channels</h2>
                <span class="hint">
                    TelegramClient refreshes from this list every 60 seconds.
                    No restart or redeploy needed.
                </span>
            </div>

            <!-- Add form -->
            <div class="add-form">
                <select v-model="addMode" class="add-mode">
                    <option value="username">By @username</option>
                    <option value="peerId">By Peer ID</option>
                </select>

                <input v-if="addMode === 'username'"
                       v-model="addUsername"
                       type="text"
                       class="add-input"
                       placeholder="@channelname"
                       @keydown.enter="onAdd" />

                <input v-else
                       v-model="addPeerId"
                       type="number"
                       class="add-input"
                       placeholder="1234567890"
                       @keydown.enter="onAdd" />

                <input v-model="addNote"
                       type="text"
                       class="add-note"
                       placeholder="Note (optional)"
                       @keydown.enter="onAdd" />

                <button @click="onAdd" :disabled="adding" class="add-btn">
                    {{ adding ? 'Adding…' : 'Add' }}
                </button>
            </div>

            <div v-if="error" class="error">{{ error }}</div>

            <!-- List -->
            <table class="wc-table" v-if="!loading">
                <thead>
                    <tr>
                        <th>Active</th>
                        <th>ID</th>
                        <th>Peer ID</th>
                        <th>@Username</th>
                        <th>Note</th>
                        <th>Updated</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in rows" :key="row.watchedChannelId" :class="{ inactive: !row.isActive }">
                        <td>
                            <input type="checkbox"
                                   :checked="row.isActive"
                                   @change="onToggle(row, ($event.target as HTMLInputElement).checked)" />
                        </td>
                        <td class="muted">{{ row.watchedChannelId }}</td>
                        <td>{{ row.peerId ?? '—' }}</td>
                        <td>{{ row.username ? '@' + row.username : '—' }}</td>
                        <td>{{ row.note || '' }}</td>
                        <td class="muted">{{ formatDate(row.updatedAt) }}</td>
                        <td>
                            <button class="del-btn" @click="onDelete(row)">Delete</button>
                        </td>
                    </tr>
                    <tr v-if="rows.length === 0">
                        <td colspan="7" class="empty">No watched channels yet — add one above.</td>
                    </tr>
                </tbody>
            </table>

            <div v-else class="loading">Loading…</div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import BackendHeader from '@/components/BackendHeader.vue'
import api from '@/api'

interface WatchedChannel {
    watchedChannelId: number
    peerId: number | null
    username: string | null
    note: string | null
    isActive: boolean
    createdAt: string
    updatedAt: string
}

const rows = ref<WatchedChannel[]>([])
const loading = ref(false)
const adding = ref(false)
const error = ref<string | null>(null)

const addMode = ref<'username' | 'peerId'>('username')
const addUsername = ref('')
const addPeerId = ref<number | null>(null)
const addNote = ref('')

const load = async () => {
    loading.value = true
    error.value = null
    try {
        const resp = await api.getWatchedChannels()
        rows.value = resp.data
    } catch (e: any) {
        error.value = e?.response?.data?.error || e?.message || 'Failed to load'
    } finally {
        loading.value = false
    }
}

const onAdd = async () => {
    error.value = null
    const payload: any = { note: addNote.value || null }
    if (addMode.value === 'username') {
        if (!addUsername.value.trim()) { error.value = 'Username required'; return }
        payload.username = addUsername.value.trim().replace(/^@/, '')
    } else {
        if (!addPeerId.value) { error.value = 'Peer ID required'; return }
        payload.peerId = addPeerId.value
    }
    adding.value = true
    try {
        await api.addWatchedChannel(payload)
        addUsername.value = ''
        addPeerId.value = null
        addNote.value = ''
        await load()
    } catch (e: any) {
        error.value = e?.response?.data?.error || e?.message || 'Failed to add'
    } finally {
        adding.value = false
    }
}

const onToggle = async (row: WatchedChannel, isActive: boolean) => {
    try {
        await api.updateWatchedChannel(row.watchedChannelId, { isActive })
        row.isActive = isActive
    } catch (e: any) {
        error.value = e?.response?.data?.error || e?.message || 'Toggle failed'
        await load()  // re-sync row state
    }
}

const onDelete = async (row: WatchedChannel) => {
    const label = row.username ? '@' + row.username : (row.peerId?.toString() ?? '?')
    if (!confirm(`Delete watchlist entry "${label}"?`)) return
    try {
        await api.deleteWatchedChannel(row.watchedChannelId)
        rows.value = rows.value.filter(r => r.watchedChannelId !== row.watchedChannelId)
    } catch (e: any) {
        error.value = e?.response?.data?.error || e?.message || 'Delete failed'
    }
}

const formatDate = (iso: string): string => {
    if (!iso) return ''
    const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso)
    const d = new Date(hasTz ? iso : iso + 'Z')
    if (isNaN(d.getTime())) return ''
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

onMounted(load)
</script>

<style scoped>
.wc-page { min-height: 100vh; background: #f5f5f5; padding: 20px; text-align: left; }
.wc-container { max-width: 1100px; margin: 0 auto; background: #fff; border-radius: 8px; padding: 20px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }

.page-header { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 14px; padding-bottom: 12px; border-bottom: 2px solid #e0e0e0; flex-wrap: wrap; gap: 12px; }
.page-header h2 { margin: 0; }
.hint { font-size: 12px; color: #888; }

.add-form { display: flex; gap: 8px; padding: 10px; background: #f0f7ff; border: 1px dashed #8ab4f0; border-radius: 6px; margin-bottom: 14px; flex-wrap: wrap; align-items: center; }
.add-mode { padding: 7px 10px; border: 1px solid #cfd8dc; border-radius: 4px; font-size: 13px; background: #fff; }
.add-input { padding: 7px 10px; border: 1px solid #cfd8dc; border-radius: 4px; font-size: 13px; min-width: 200px; }
.add-note { padding: 7px 10px; border: 1px solid #cfd8dc; border-radius: 4px; font-size: 13px; flex: 1; min-width: 150px; }
.add-btn { padding: 7px 16px; background: #1565c0; color: #fff; border: none; border-radius: 4px; font-size: 13px; font-weight: 600; cursor: pointer; }
.add-btn:hover:not(:disabled) { background: #0d47a1; }
.add-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.error { color: #c62828; background: #ffebee; padding: 8px 12px; border-radius: 4px; margin-bottom: 12px; font-size: 13px; }
.loading, .empty { text-align: center; padding: 40px; color: #999; font-size: 14px; }

.wc-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.wc-table th, .wc-table td { padding: 8px 10px; text-align: left; border-bottom: 1px solid #eee; }
.wc-table th { background: #fafafa; font-weight: 600; color: #555; font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; }
.wc-table tr.inactive { opacity: 0.5; background: #fafafa; }
.wc-table .muted { color: #999; font-size: 12px; }
.del-btn { padding: 4px 10px; background: none; border: 1px solid #ef9a9a; color: #c62828; border-radius: 3px; font-size: 12px; cursor: pointer; }
.del-btn:hover { background: #ffebee; }
</style>
